// Pass 5 — /run/matrix
//
// Analyst persona's relief valve. Pick threats × envs (× guidance),
// kick a batch of cells in one go, land on /run/matrix/:id with a
// live grid. Same engine path as Quick Sim and the Workbench
// (Pass 2 scenario_id mode), no shadow runs.
//
// Composer flow:
//   1. Base: scenario OR (interceptor + engagement)
//   2. Threats axis (1..N)
//   3. Environments axis (1..M)
//   4. (optional) Guidance profiles axis (1..K)
//   5. Settings: seeds_per_cell slider + dynamics_backend toggle
//   6. Live preview of total cells + total seed-runs
//   7. Run matrix → POST /api/sim/matrix/compose-and-run →
//      navigate(`/run/matrix/${id}`)
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Grid3x3, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { formatApiError } from "@/lib/formatApiError";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import PageHeader from "@/components/PageHeader";
import SpinnerButton from "@/components/SpinnerButton";
import { sim } from "@/api/endpoints";

const DEFAULT_SEEDS_PER_CELL = 16;
const WARN_TOTAL = 200;

// --------------------------------------------------------------------
// Visual primitives
// --------------------------------------------------------------------
function AxisCheckList({ label, items, selected, onToggle, testIdPrefix, getLabel, getMeta }) {
    const lbl = getLabel || ((it) => it.name);
    const meta = getMeta || (() => null);
    return (
        <div className="space-y-2" data-testid={`matrix-axis-${testIdPrefix}`}>
            <div className="flex items-center justify-between">
                <h3 className="text-[14px] font-medium text-ux-text-1">
                    {label}
                </h3>
                <span className="text-[11px] font-mono text-ux-text-3">
                    {selected.length} / {items.length}
                </span>
            </div>
            <div className="grid gap-1.5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-h-72 overflow-y-auto pr-1">
                {items.length === 0 && (
                    <div className="text-[12px] text-ux-text-3 italic">
                        No options available.
                    </div>
                )}
                {items.map((it) => {
                    const id = it.id;
                    const active = selected.includes(id);
                    return (
                        <label
                            key={id}
                            className={[
                                "flex items-start gap-2 p-2 rounded border text-[12px] cursor-pointer transition-colors",
                                active
                                    ? "border-ux-accent bg-ux-accent/5 text-ux-text-1"
                                    : "border-ux-border-1 bg-ux-bg-1 hover:bg-ux-bg-2 text-ux-text-2",
                            ].join(" ")}
                        >
                            <Checkbox
                                checked={active}
                                onCheckedChange={() => onToggle(id)}
                                data-testid={`matrix-axis-${testIdPrefix}-${id}`}
                                className="mt-0.5"
                            />
                            <div className="flex-1 min-w-0">
                                <div className="truncate">{lbl(it)}</div>
                                {meta(it) && (
                                    <div className="text-[10px] text-ux-text-3 font-mono mt-0.5 truncate">
                                        {meta(it)}
                                    </div>
                                )}
                            </div>
                        </label>
                    );
                })}
            </div>
        </div>
    );
}

// --------------------------------------------------------------------
// Page
// --------------------------------------------------------------------
export default function MatrixRunPage() {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [scenarios, setScenarios] = useState([]);
    const [airframes, setAirframes] = useState([]);
    const [engagements, setEngagements] = useState([]);
    const [threats, setThreats] = useState([]);
    const [environments, setEnvironments] = useState([]);
    const [guidanceProfiles, setGuidanceProfiles] = useState([]);
    const [loadErr, setLoadErr] = useState(null);

    // Form state
    const [baseMode, setBaseMode] = useState("interceptor_engagement");
    const [baseScenarioId, setBaseScenarioId] = useState("");
    const [baseInterceptorId, setBaseInterceptorId] = useState("");
    const [baseEngagementId, setBaseEngagementId] = useState("");
    const [threatIds, setThreatIds] = useState([]);
    const [environmentIds, setEnvironmentIds] = useState([]);
    const [guidanceEnabled, setGuidanceEnabled] = useState(false);
    const [guidanceIds, setGuidanceIds] = useState([]);
    const [seedsPerCell, setSeedsPerCell] = useState(DEFAULT_SEEDS_PER_CELL);
    const [useJsbsim, setUseJsbsim] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [warnDialogOpen, setWarnDialogOpen] = useState(false);

    // ----- bootstrap data -----
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [scn, af, eng, th, env, gp] = await Promise.all([
                    sim.scenarios.list({ scope: "tenant" }),
                    sim.airframes.list({ scope: "preset" }),
                    sim.engagements.list({ scope: "preset" }),
                    sim.threats.list({ scope: "preset" }),
                    sim.environments.list({ scope: "preset" }),
                    sim.guidanceProfiles.list({ scope: "preset" }),
                ]);
                if (cancelled) return;
                setScenarios(scn);
                setAirframes(af);
                setEngagements(eng);
                setThreats(th);
                setEnvironments(env);
                setGuidanceProfiles(gp);
                // Sensible defaults
                if (af[0]) setBaseInterceptorId(af[0].id);
                if (eng[0]) setBaseEngagementId(eng[0].id);
            } catch (e) {
                if (!cancelled)
                    setLoadErr(
                        formatApiError(e, "Failed to load picker data"),
                    );
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const totalCells = useMemo(() => {
        const t = threatIds.length;
        const e = environmentIds.length;
        const g = guidanceEnabled ? guidanceIds.length : 1;
        if (t === 0 || e === 0 || (guidanceEnabled && g === 0)) return 0;
        return t * e * g;
    }, [threatIds, environmentIds, guidanceEnabled, guidanceIds]);
    const totalSeedRuns = totalCells * seedsPerCell;
    const overWarn = totalSeedRuns > WARN_TOTAL;

    const baseValid = useMemo(() => {
        if (baseMode === "scenario") return Boolean(baseScenarioId);
        return Boolean(baseInterceptorId && baseEngagementId);
    }, [baseMode, baseScenarioId, baseInterceptorId, baseEngagementId]);

    const canRun =
        !submitting &&
        baseValid &&
        threatIds.length > 0 &&
        environmentIds.length > 0 &&
        (!guidanceEnabled || guidanceIds.length > 0) &&
        totalCells > 0;

    const submit = useCallback(
        async ({ confirmOverWarn }) => {
            setSubmitting(true);
            try {
                const body = {
                    base:
                        baseMode === "scenario"
                            ? { mode: "scenario", scenario_id: baseScenarioId }
                            : {
                                  mode: "interceptor_engagement",
                                  interceptor_airframe_id: baseInterceptorId,
                                  engagement_id: baseEngagementId,
                              },
                    threat_ids: threatIds,
                    environment_ids: environmentIds,
                    guidance_profile_ids: guidanceEnabled ? guidanceIds : null,
                    seeds_per_cell: seedsPerCell,
                    dynamics_backend: useJsbsim ? "jsbsim_hybrid" : "kinematic",
                    confirm_over_warn: !!confirmOverWarn,
                };
                const out = await sim.matrix.composeAndRun(body);
                navigate(`/run/matrix/${out.matrix_job_id}`);
            } catch (e) {
                const detail = e?.response?.data?.detail;
                if (
                    detail &&
                    typeof detail === "object" &&
                    detail.error === "matrix_size_warning"
                ) {
                    setWarnDialogOpen(true);
                    return;
                }
                if (
                    detail &&
                    typeof detail === "object" &&
                    Array.isArray(detail.blocking)
                ) {
                    toast.error(
                        `Matrix blocked: ${detail.blocking[0]?.message || "validation gate"}`,
                    );
                    return;
                }
                toast.error(
                    (typeof detail === "string" && detail) ||
                        e?.message ||
                        "Failed to start the matrix.",
                );
            } finally {
                setSubmitting(false);
            }
        },
        [
            baseEngagementId,
            baseInterceptorId,
            baseMode,
            baseScenarioId,
            environmentIds,
            guidanceEnabled,
            guidanceIds,
            navigate,
            seedsPerCell,
            threatIds,
            useJsbsim,
        ],
    );

    const onRunClick = useCallback(() => {
        if (overWarn) {
            setWarnDialogOpen(true);
            return;
        }
        submit({ confirmOverWarn: false });
    }, [overWarn, submit]);

    if (loading) {
        return (
            <div
                className="flex items-center justify-center h-[40vh] text-ux-text-3 font-mono text-[13px]"
                data-testid="matrix-page-loading"
            >
                Loading matrix composer…
            </div>
        );
    }
    if (loadErr) {
        return (
            <div className="space-y-4" data-testid="matrix-page">
                <PageHeader
                    title="Matrix Run"
                    subtitle="N×M batch kickoff for scenario sweeps."
                />
                <div className="rounded-md border border-ux-danger/40 bg-ux-danger-soft/40 p-4 text-ux-danger text-[13px]">
                    {String(loadErr)}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="matrix-page">
            <PageHeader
                title="Matrix Run"
                subtitle="Pick threats × environments (× guidance). Kick a batch in one go. Land on a results grid."
            />

            {/* ── Base ──────────────────────────────────────────────── */}
            <section
                className="rounded-xl border border-ux-border-1 bg-ux-bg-1 p-4 space-y-4"
                data-testid="matrix-base-section"
            >
                <div className="flex items-center gap-3">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-ux-text-3 font-mono">
                        Base
                    </div>
                    <h2 className="text-[14px] text-ux-text-1 font-medium">
                        Invariant axis
                    </h2>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <label className="flex items-center gap-2 text-[12px] text-ux-text-2 cursor-pointer">
                        <input
                            type="radio"
                            name="matrix-base-mode"
                            data-testid="matrix-base-mode-scenario"
                            checked={baseMode === "scenario"}
                            onChange={() => setBaseMode("scenario")}
                            className="accent-ux-accent"
                        />
                        Start from a scenario
                    </label>
                    <label className="flex items-center gap-2 text-[12px] text-ux-text-2 cursor-pointer">
                        <input
                            type="radio"
                            name="matrix-base-mode"
                            data-testid="matrix-base-mode-spine"
                            checked={baseMode === "interceptor_engagement"}
                            onChange={() =>
                                setBaseMode("interceptor_engagement")
                            }
                            className="accent-ux-accent"
                        />
                        Start from an interceptor + engagement
                    </label>
                </div>
                {baseMode === "scenario" ? (
                    <div className="max-w-xl">
                        <Select
                            value={baseScenarioId}
                            onValueChange={setBaseScenarioId}
                        >
                            <SelectTrigger
                                data-testid="matrix-base-scenario-select"
                                className="bg-ux-bg-1 border-ux-border-1"
                            >
                                <SelectValue placeholder="Pick a scenario…" />
                            </SelectTrigger>
                            <SelectContent>
                                {scenarios.map((s) => (
                                    <SelectItem key={s.id} value={s.id}>
                                        {s.name}
                                        {s.is_preset ? " · preset" : ""}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
                        <Select
                            value={baseInterceptorId}
                            onValueChange={setBaseInterceptorId}
                        >
                            <SelectTrigger
                                data-testid="matrix-base-interceptor-select"
                                className="bg-ux-bg-1 border-ux-border-1"
                            >
                                <SelectValue placeholder="Interceptor…" />
                            </SelectTrigger>
                            <SelectContent>
                                {airframes.map((a) => (
                                    <SelectItem key={a.id} value={a.id}>
                                        {a.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select
                            value={baseEngagementId}
                            onValueChange={setBaseEngagementId}
                        >
                            <SelectTrigger
                                data-testid="matrix-base-engagement-select"
                                className="bg-ux-bg-1 border-ux-border-1"
                            >
                                <SelectValue placeholder="Engagement…" />
                            </SelectTrigger>
                            <SelectContent>
                                {engagements.map((e) => (
                                    <SelectItem key={e.id} value={e.id}>
                                        {e.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}
            </section>

            {/* ── Axes ──────────────────────────────────────────────── */}
            <section
                className="rounded-xl border border-ux-border-1 bg-ux-bg-1 p-4 space-y-6"
                data-testid="matrix-axes-section"
            >
                <div className="flex items-center gap-3">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-ux-text-3 font-mono">
                        Axes
                    </div>
                    <h2 className="text-[14px] text-ux-text-1 font-medium">
                        Pick what varies
                    </h2>
                </div>

                <AxisCheckList
                    label="Threats"
                    items={threats}
                    selected={threatIds}
                    onToggle={(id) =>
                        setThreatIds((prev) =>
                            prev.includes(id)
                                ? prev.filter((x) => x !== id)
                                : [...prev, id],
                        )
                    }
                    testIdPrefix="threat"
                    getMeta={(t) => t.airframe_id || t.role}
                />
                <AxisCheckList
                    label="Environments"
                    items={environments}
                    selected={environmentIds}
                    onToggle={(id) =>
                        setEnvironmentIds((prev) =>
                            prev.includes(id)
                                ? prev.filter((x) => x !== id)
                                : [...prev, id],
                        )
                    }
                    testIdPrefix="environment"
                />
                <div className="space-y-2">
                    <div className="flex items-center gap-3">
                        <Switch
                            checked={guidanceEnabled}
                            onCheckedChange={setGuidanceEnabled}
                            data-testid="matrix-guidance-toggle"
                        />
                        <span className="text-[13px] text-ux-text-2">
                            Add a guidance axis (off by default — keeps the
                            matrix small)
                        </span>
                    </div>
                    {guidanceEnabled && (
                        <AxisCheckList
                            label="Guidance profiles"
                            items={guidanceProfiles}
                            selected={guidanceIds}
                            onToggle={(id) =>
                                setGuidanceIds((prev) =>
                                    prev.includes(id)
                                        ? prev.filter((x) => x !== id)
                                        : [...prev, id],
                                )
                            }
                            testIdPrefix="guidance"
                            getMeta={(g) => g.law || g.kind}
                        />
                    )}
                </div>
            </section>

            {/* ── Settings ──────────────────────────────────────────── */}
            <section
                className="rounded-xl border border-ux-border-1 bg-ux-bg-1 p-4 space-y-4"
                data-testid="matrix-settings-section"
            >
                <div className="flex items-center gap-3">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-ux-text-3 font-mono">
                        Settings
                    </div>
                    <h2 className="text-[14px] text-ux-text-1 font-medium">
                        Execution
                    </h2>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-3xl">
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <label className="text-[12px] text-ux-text-2">
                                Seeds per cell
                            </label>
                            <span
                                className="font-mono text-[12px] text-ux-text-1 tabular-nums"
                                data-testid="matrix-seeds-value"
                            >
                                {seedsPerCell}
                            </span>
                        </div>
                        <Slider
                            value={[seedsPerCell]}
                            onValueChange={(v) => setSeedsPerCell(v[0] ?? 16)}
                            min={1}
                            max={64}
                            step={1}
                            data-testid="matrix-seeds-slider"
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="text-[12px] text-ux-text-2">
                            Dynamics backend
                        </label>
                        <div className="flex items-center gap-3">
                            <Switch
                                checked={useJsbsim}
                                onCheckedChange={setUseJsbsim}
                                data-testid="matrix-jsbsim-toggle"
                            />
                            <span className="text-[12px] text-ux-text-2 font-mono">
                                {useJsbsim
                                    ? "jsbsim_hybrid (approximate — only interceptor_a calibrated)"
                                    : "kinematic (default — deterministic)"}
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Sticky preview + run ──────────────────────────────── */}
            <div
                className="sticky bottom-0 -mx-4 sm:-mx-6 lg:-mx-8 border-t border-ux-border-1 bg-ux-bg-1/90 backdrop-blur-sm px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4 flex-wrap"
                data-testid="matrix-actionbar"
            >
                <div
                    className="flex items-center gap-2 text-[13px] text-ux-text-2"
                    data-testid="matrix-preview"
                >
                    <Grid3x3 className="w-4 h-4 text-ux-accent" aria-hidden />
                    <span>
                        {threatIds.length} threats × {environmentIds.length} envs
                        {guidanceEnabled
                            ? ` × ${guidanceIds.length} guidance`
                            : ""}
                        &nbsp;=&nbsp;
                        <strong className="text-ux-text-1">
                            {totalCells} cells
                        </strong>
                        &nbsp;·&nbsp;
                        <strong
                            className={
                                overWarn ? "text-ux-warning" : "text-ux-text-1"
                            }
                        >
                            {totalSeedRuns} seed-runs
                        </strong>
                    </span>
                    {overWarn && (
                        <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-mono text-ux-warning">
                            <AlertTriangle className="w-3 h-3" aria-hidden />
                            over the {WARN_TOTAL} threshold
                        </span>
                    )}
                </div>
                <SpinnerButton
                    disabled={!canRun}
                    loading={submitting}
                    onClick={onRunClick}
                    testId="matrix-run-button"
                >
                    <PlayCircle className="w-3.5 h-3.5" />
                    Run matrix
                </SpinnerButton>
            </div>

            {/* ── Over-warning confirm dialog ───────────────────────── */}
            <AlertDialog
                open={warnDialogOpen}
                onOpenChange={setWarnDialogOpen}
            >
                <AlertDialogContent
                    className="bg-ux-bg-1 border-ux-border-1"
                    data-testid="matrix-warn-dialog"
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            That's a big matrix.
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            You're about to launch&nbsp;
                            <strong>{totalSeedRuns}</strong> seed-runs across{" "}
                            <strong>{totalCells}</strong> cells. The soft
                            warning threshold is {WARN_TOTAL}. Proceed only if
                            you've got time to wait — the run worker pipelines
                            against the existing concurrency limit.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="matrix-warn-cancel">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="matrix-warn-confirm"
                            onClick={() =>
                                submit({ confirmOverWarn: true })
                            }
                        >
                            Run anyway
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
